import { FILE_BASE_URL } from "config/apiConfig";
import React from "react";
import { IconCorporateFareBlue } from "assets/svg";
import { RankingItem } from "reducers/rankingApi";
import RankBadge from "./RankBadge";
import {
  formatWeightUnit,
  formatWeightValue,
} from "common/helper/quantity.helper";

interface RankingTableProps {
  rows: RankingItem[];
  isLoading: boolean;
  errorMessage: string;
}

const KilogramValue = ({
  value,
  compact = false,
  className = "",
}: {
  value: number;
  compact?: boolean;
  className?: string;
}) => (
  <span
    className={`${
      compact
        ? "inline-flex items-baseline gap-1"
        : "grid w-[8.5rem] grid-cols-[6rem_max-content] items-baseline gap-1"
    } ${className}`}
  >
    <span className={compact ? "text-left" : "text-right"}>
      {formatWeightValue(value)}
    </span>
    <span className="text-left">{formatWeightUnit(value)}</span>
  </span>
);

const getFileUrl = (path: string) => {
  if (/^https?:\/\//.test(path)) return path;
  return `${FILE_BASE_URL}/${path.replace(/^\/+/, "")}`;
};

const MobileCompanyIdentity = ({ row }: { row: RankingItem }) => (
  <div className="flex min-w-0 items-center gap-2">
    {row.company_logo_url ? (
      <img
        src={getFileUrl(row.company_logo_url)}
        alt=""
        className="h-9 w-9 shrink-0 rounded-full bg-[#eef5fa] object-cover"
      />
    ) : (
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#eef5fa]">
        <IconCorporateFareBlue
          aria-hidden="true"
          className="h-5 w-5 text-[#1b4b86]"
        />
      </span>
    )}
    <span className="min-w-0 break-words text-sm">
      {row.company_name || "-"}
    </span>
  </div>
);

const MobileLevel = ({ row }: { row: RankingItem }) => (
  <div className="flex items-center gap-2.5">
    <span className="min-w-[3.75rem] font-['Audiowide'] text-lg">
      Lv.{row.level}
    </span>
    <span className="h-1.5 w-[6.25rem] overflow-hidden rounded bg-[#eef5fa]">
      <span
        role="progressbar"
        aria-label={`Lv.${row.level}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={row.level}
        className="block h-full"
        style={{
          width: `${Math.min(row.level, 100)}%`,
          backgroundImage: "linear-gradient(90deg, #D4F754 0%, #2CDCEC 100%)",
        }}
      />
    </span>
  </div>
);

const MobileRankingList = ({
  rows,
  isLoading,
  errorMessage,
}: RankingTableProps) => (
  <div className="overflow-hidden rounded bg-white md:hidden">
    {isLoading ? (
      <div className="px-5 py-12 text-center text-[#64748b]">読み込み中...</div>
    ) : errorMessage ? (
      <div className="px-5 py-12 text-center text-[#b42318]">
        {errorMessage}
      </div>
    ) : rows.length > 0 ? (
      rows.map((row) => (
        <article
          key={row.company_id}
          className="grid grid-cols-[1.875rem_minmax(0,1fr)] items-center gap-5 border-b border-[#d5d5d5] p-4 last:border-b-0"
        >
          <RankBadge rank={row.rank} />
          <div className="flex min-w-0 flex-col gap-2.5">
            <MobileCompanyIdentity row={row} />
            <MobileLevel row={row} />
            <div className="rounded-md bg-[#f5f5f5] px-4 py-3 text-xs">
              <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center pb-1.5">
                <span>構造スコア</span>
                <span className="font-['Audiowide'] text-lg leading-none">
                  {row.total_xp.toLocaleString("en-US")}
                </span>
              </div>
              <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center pt-1.5">
                <span>循環量</span>
                <KilogramValue
                  value={row.total_volume_kg}
                  compact
                  className="font-['Audiowide'] text-sm"
                />
              </div>
            </div>
          </div>
        </article>
      ))
    ) : (
      <div className="flex min-h-[12.5rem] flex-col items-center justify-center gap-2 px-5 text-center">
        <p className="text-base font-bold text-primary">
          ランキングデータがありません
        </p>
        <p className="text-[0.8125rem] text-[#333333]">
          現在、表示できるランキングデータが蓄積されていません。
        </p>
      </div>
    )}
  </div>
);

const RankingTable = ({ rows, isLoading, errorMessage }: RankingTableProps) => (
  <>
    <MobileRankingList
      rows={rows}
      isLoading={isLoading}
      errorMessage={errorMessage}
    />
    <div className="hidden overflow-x-auto md:block">
      <table className="w-full min-w-[850px] border-collapse">
        <thead>
          <tr className="text-left text-[14px] font-bold text-white">
            <th className="w-[90px] px-[15px] py-[14px] text-center">順位</th>
            <th className="px-[15px] py-[14px]">企業名</th>
            <th className="w-[200px] px-[15px] py-[14px]">レベル</th>
            <th className="w-[140px] px-[15px] py-[14px]">構造スコア</th>
            <th className="w-[170px] px-[15px] py-[14px] text-center">
              循環量
            </th>
          </tr>
        </thead>
        <tbody className="bg-white">
          {isLoading ? (
            <tr>
              <td
                colSpan={5}
                className="px-[20px] py-[50px] text-center text-[#64748b]"
              >
                読み込み中...
              </td>
            </tr>
          ) : errorMessage ? (
            <tr>
              <td
                colSpan={5}
                className="px-[20px] py-[50px] text-center text-[#b42318]"
              >
                {errorMessage}
              </td>
            </tr>
          ) : rows.length > 0 ? (
            rows.map((row) => (
              <tr
                key={row.company_id}
                className="border-b border-[#d5d5d5] last:border-b-0"
              >
                <td className="px-[15px] py-[15px] text-center">
                  <RankBadge rank={row.rank} />
                </td>
                <td className="px-[15px] py-[15px]">
                  <div className="flex items-center gap-[10px]">
                    {row.company_logo_url ? (
                      <img
                        src={getFileUrl(row.company_logo_url)}
                        alt=""
                        className="h-[36px] w-[36px] shrink-0 rounded-full bg-[#eef5fa] object-cover"
                      />
                    ) : (
                      <span className="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full bg-[#eef5fa] text-[10px] font-bold text-white">
                        <IconCorporateFareBlue
                          aria-hidden="true"
                          className="h-[20px] w-[20px] text-[#1b4b86]"
                        />
                      </span>
                    )}
                    <span className="text-[14px]">
                      {row.company_name || "-"}
                    </span>
                  </div>
                </td>
                <td className="px-[15px] py-[15px]">
                  <div className="flex items-center gap-[10px]">
                    <span className="min-w-[60px] font-['Audiowide'] text-[18px]">
                      Lv.{row.level}
                    </span>
                    <span className="h-[6px] w-[100px] overflow-hidden rounded-[3px] bg-[#eef5fa]">
                      <span
                        role="progressbar"
                        aria-label={`Lv.${row.level}`}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={row.level}
                        className="block h-full"
                        style={{
                          width: `${Math.min(row.level, 100)}%`,
                          backgroundImage:
                            "linear-gradient(90deg, #D4F754 0%, #2CDCEC 100%)",
                        }}
                      />
                    </span>
                  </div>
                </td>
                <td className="px-[15px] py-[15px] font-['Audiowide'] text-[18px]">
                  {row.total_xp.toLocaleString("en-US")}
                </td>
                <td className="px-[15px] py-[15px] font-['Audiowide'] text-[18px]">
                  <KilogramValue
                    value={row.total_volume_kg}
                    className="mx-auto"
                  />
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5} className="h-[200px] px-[20px] text-center">
                <div className="flex flex-col items-center justify-center gap-[8px]">
                  <p className="text-[16px] font-bold text-primary">
                    ランキングデータがありません
                  </p>
                  <p className="text-[13px] text-[#333333]">
                    現在、表示できるランキングデータが蓄積されていません。
                  </p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </>
);

export default RankingTable;
